<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import api from "@/services/api";
import SocialLinksForm from "./SocialLinksForm.vue";

// ✅ controle de visibilidade e seção (personal | social)
const props = defineProps({
  isVisible: { type: Boolean, default: true },
  section: {
    type: String,
    default: "personal", // 'personal' | 'social'
  },
});

// 🎯 evento 'formClosed'
const emit = defineEmits(["formClosed"]);

const userStore = useUserStore();

const formData = ref({
  userId: null,
  fullName: "",
  userName: "",
  email: "",
  birthDate: "",
  ddi: "",
  ddd: "",
  country: "",
  profession: "",
  photoUrl: "",
  phone: "",
  phoneIsWhatsapp: false,
  isWhatsappPublic: false,
  telegramUsername: "",
  instagramUsername: "",
  linkedInUsername: "",
  facebookUsername: "",
  // ❌ senha REMOVIDA – agora é só na aba Alterar Senha
  // ── MUDANÇA: groups removido daqui — exibido em AdditionalProfileDetails ──
});

const selectedFile = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout(duration) {
  return new Promise((resolve) => {
    if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = setTimeout(() => {
      showToast.value = false;
      toastTimeoutId.value = null;
      resolve();
    }, duration);
  });
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

async function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  await resetToastTimeout(2400);
}

/**
 * 🎯 Fecha o formulário (e o modal pai)
 */
function closeForm() {
  if (toastTimeoutId.value) {
    clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = null;
  }
  emit("formClosed");
}

// ====================================================================

const isLoading = ref(false);

const countries = ref([
  { code: "355", name: "Albânia" },
  { code: "49", name: "Alemanha" },
  { code: "376", name: "Andorra" },
  { code: "54", name: "Argentina" },
  { code: "374", name: "Armênia" },
  { code: "43", name: "Áustria" },
  { code: "994", name: "Azerbaijão" },
  { code: "1-242", name: "Bahamas" },
  { code: "1-246", name: "Barbados" },
  { code: "32", name: "Bélgica" },
  { code: "501", name: "Belize" },
  { code: "375", name: "Bielorrússia" },
  { code: "591", name: "Bolívia" },
  { code: "387", name: "Bósnia e Herzegovina" },
  { code: "55", name: "Brasil" },
  { code: "359", name: "Bulgária" },
  { code: "1", name: "Canadá" },
  { code: "357", name: "Chipre" },
  { code: "86", name: "China" },
  { code: "57", name: "Colômbia" },
  { code: "506", name: "Costa Rica" },
  { code: "385", name: "Croácia" },
  { code: "53", name: "Cuba" },
  { code: "45", name: "Dinamarca" },
  { code: "1-767", name: "Dominica" },
  { code: "503", name: "El Salvador" },
  { code: "593", name: "Equador" },
  { code: "421", name: "Eslováquia" },
  { code: "386", name: "Eslovênia" },
  { code: "34", name: "Espanha" },
  { code: "1", name: "Estados Unidos" },
  { code: "372", name: "Estônia" },
  { code: "358", name: "Finlândia" },
  { code: "33", name: "França" },
  { code: "995", name: "Geórgia" },
  { code: "350", name: "Gibraltar" },
  { code: "30", name: "Grécia" },
  { code: "1-473", name: "Granada" },
  { code: "502", name: "Guatemala" },
  { code: "592", name: "Guiana" },
  { code: "509", name: "Haiti" },
  { code: "504", name: "Honduras" },
  { code: "36", name: "Hungria" },
  { code: "353", name: "Irlanda" },
  { code: "354", name: "Islândia" },
  { code: "39", name: "Itália" },
  { code: "1-876", name: "Jamaica" },
  { code: "81", name: "Japão" },
  { code: "371", name: "Letônia" },
  { code: "423", name: "Liechtenstein" },
  { code: "370", name: "Lituânia" },
  { code: "352", name: "Luxemburgo" },
  { code: "356", name: "Malta" },
  { code: "52", name: "México" },
  { code: "373", name: "Moldávia" },
  { code: "377", name: "Mônaco" },
  { code: "382", name: "Montenegro" },
  { code: "505", name: "Nicarágua" },
  { code: "47", name: "Noruega" },
  { code: "31", name: "Países Baixos" },
  { code: "507", name: "Panamá" },
  { code: "595", name: "Paraguai" },
  { code: "51", name: "Peru" },
  { code: "48", name: "Polônia" },
  { code: "1-787", name: "Porto Rico" },
  { code: "351", name: "Portugal" },
  { code: "44", name: "Reino Unido" },
  { code: "420", name: "República Tcheca" },
  { code: "1-809", name: "República Dominicana" },
  { code: "40", name: "Romênia" },
  { code: "7", name: "Rússia" },
  { code: "378", name: "San Marino" },
  { code: "1-869", name: "São Cristóvão e Nevis" },
  { code: "1-758", name: "Santa Lúcia" },
  { code: "1-784", name: "São Vicente e Granadinas" },
  { code: "381", name: "Sérvia" },
  { code: "46", name: "Suécia" },
  { code: "41", name: "Suíça" },
  { code: "597", name: "Suriname" },
  { code: "1-868", name: "Trinidad e Tobago" },
  { code: "90", name: "Turquia" },
  { code: "380", name: "Ucrânia" },
  { code: "598", name: "Uruguai" },
  { code: "379", name: "Vaticano" },
  { code: "58", name: "Venezuela" },
]);

const defaultCountry =
  countries.value.find((c) => c.code === "55") || countries.value[0];

const phoneParts = ref({
  selectedCountry: defaultCountry,
});

const ddiSearchTerm = ref("");
const isAutocompleteVisible = ref(false);
const ddiAutocompleteRef = ref(null);
const activeIndex = ref(-1);
const ddiOptionsRef = ref(null);

const filteredCountries = computed(() => {
  const selectedCountryText = `+${phoneParts.value.selectedCountry.code}`;
  if (!ddiSearchTerm.value || ddiSearchTerm.value === selectedCountryText) {
    return countries.value;
  }
  const searchTerm = ddiSearchTerm.value.toLowerCase();
  return countries.value.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm) ||
      country.code.includes(searchTerm)
  );
});

function selectCountry(country) {
  phoneParts.value.selectedCountry = country;
  ddiSearchTerm.value = `+${country.code}`;
  isAutocompleteVisible.value = false;
  activeIndex.value = -1;
}

function handleDdiFocus() {
  ddiSearchTerm.value = "";
  isAutocompleteVisible.value = true;
  activeIndex.value = 0;
}

function handleDdiInput() {
  isAutocompleteVisible.value = true;
  activeIndex.value = 0;
}

function handleDdiBlur() {
  setTimeout(() => {
    isAutocompleteVisible.value = false;
    const selected = phoneParts.value.selectedCountry;
    ddiSearchTerm.value = `+${selected.code}`;
    activeIndex.value = -1;
  }, 200);
}

function scrollActiveIntoView() {
  const el = ddiOptionsRef.value?.querySelector?.(".is-active");
  el?.scrollIntoView({ block: "nearest" });
}

function handleDdiKeydown(e) {
  if (e.key === "Enter") {
    e.preventDefault();
    e.stopPropagation();

    if (isAutocompleteVisible.value) {
      const list = filteredCountries.value || [];
      if (!list.length) return;

      const country = list[activeIndex.value] || list[0];
      if (country) selectCountry(country);
    }
    return;
  }

  if (!isAutocompleteVisible.value) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      isAutocompleteVisible.value = true;
      activeIndex.value = 0;
      e.preventDefault();
    }
    return;
  }

  const list = filteredCountries.value || [];
  if (!list.length) return;

  if (e.key === "ArrowDown") {
    e.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % list.length;
    scrollActiveIntoView();
    return;
  }

  if (e.key === "ArrowUp") {
    e.preventDefault();
    activeIndex.value = (activeIndex.value - 1 + list.length) % list.length;
    scrollActiveIntoView();
    return;
  }

  if (e.key === "Escape") {
    e.preventDefault();
    isAutocompleteVisible.value = false;
    activeIndex.value = -1;
  }
}

const handleClickOutside = (event) => {
  if (
    ddiAutocompleteRef.value &&
    !ddiAutocompleteRef.value.contains(event.target)
  ) {
    isAutocompleteVisible.value = false;
    activeIndex.value = -1;
  }
};

watch(
  () => phoneParts.value.selectedCountry,
  (newCountry) => {
    if (newCountry) {
      formData.value.ddi = newCountry.code;
      if (newCountry.code !== "55") {
        formData.value.ddd = "";
      }
    }
  },
  { deep: true }
);

watch(
  () => formData.value.phoneIsWhatsapp,
  (isWhatsapp) => {
    if (isWhatsapp === false) {
      formData.value.isWhatsappPublic = false;
    }
  }
);

watch(
  () => formData.value.isWhatsappPublic,
  (isPublic) => {
    if (isPublic === true) {
      formData.value.phoneIsWhatsapp = true;
    }
  }
);

function extractPhoneFromE164(phoneE164, dtoCountry) {
  if (!phoneE164) {
    return { ddi: "", ddd: "", phone: "", country: "" };
  }

  const digits = phoneE164.replace(/\D/g, "");
  if (!digits) {
    return { ddi: "", ddd: "", phone: "", country: "" };
  }

  let country =
    countries.value.find((c) => c.name === dtoCountry) || defaultCountry;

  const ddiDigits = country.code.replace(/\D/g, "");

  let phone = digits;
  let ddi = country.code;
  let ddd = "";

  if (digits.startsWith(ddiDigits)) {
    let remainingDigits = digits.slice(ddiDigits.length);

    if (ddi === "55" && remainingDigits.length >= 2) {
      ddd = remainingDigits.substring(0, 2);
      phone = remainingDigits.slice(2);
    } else {
      phone = remainingDigits;
    }
  }

  return { ddi, ddd, phone, country };
}

const canSubmit = computed(() => {
  const userName = formData.value.userName?.trim();
  const email = formData.value.email?.trim();
  return !!userName && !!email;
});

const avatarSrc = computed(() => {
  let raw = formData.value.photoUrl || "";

  if (!raw) return "http://localhost:5196/userphotos/avatar_colorido.png";

  if (raw.startsWith("http") || raw.startsWith("blob:")) {
    return raw;
  }

  let base = api.defaults.baseURL || "";
  base = base.replace(/\/api$/, "").replace(/\/+$/, "");

  if (!raw.startsWith("/")) {
    raw = "/" + raw;
  }

  const finalUrl = base + raw;
  return finalUrl;
});

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);

  if (!userStore.currentUser?.userId) {
    showErrorToast(
      "Não foi possível carregar os dados do usuário. ID do usuário ausente."
    );
    return;
  }

  try {
    const dto = await userStore.getUserById(userStore.currentUser.userId);

    const birthDate =
      dto.birthDate && typeof dto.birthDate === "string"
        ? dto.birthDate.substring(0, 10)
        : "";

    const photoUrl =
      dto.photoPath && dto.photoPath.trim().length > 0
        ? dto.photoPath
        : formData.value.photoUrl;

    const { ddi, ddd, phone, country } = extractPhoneFromE164(
      dto.phoneE164,
      dto.country
    );

    const initialCountry = country || defaultCountry;
    phoneParts.value.selectedCountry = initialCountry;
    ddiSearchTerm.value = `+${initialCountry.code}`;

    Object.assign(formData.value, {
      userId: dto.userId,
      fullName: dto.fullName || "",
      userName: dto.userName || "",
      email: dto.email || "",
      birthDate,
      ddi: ddi || initialCountry.code,
      ddd: ddd || "",
      country: dto.country || "",
      profession: dto.profession || "",
      photoUrl,
      phone: phone || "",
      phoneIsWhatsapp: !!dto.phoneIsWhatsapp,
      isWhatsappPublic: !!dto.isWhatsappPublic,
      telegramUsername: dto.telegramUsername || "",
      instagramUsername: dto.instagramUsername || "",
      linkedInUsername: dto.linkedInUsername || "",
      facebookUsername: dto.facebookUsername || "",
      // ── MUDANÇA: groups removido — não é mais gerenciado aqui ────────────
    });
  } catch (error) {
    console.error("Erro ao carregar dados do usuário no ProfileForm:", error);
    showErrorToast(
      error.message || "Não foi possível carregar os dados do usuário."
    );
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

async function handleUpdate(additionalData) {
  isLoading.value = true;

  try {
    const userId = formData.value.userId || userStore.currentUser?.userId;

    if (!userId) {
      throw new Error("Usuário inválido para atualização.");
    }

    const ddiDigits = String(formData.value.ddi || "").replace(/\D/g, "");
    const dddDigits = String(formData.value.ddd || "").replace(/\D/g, "");
    const phoneDigits = String(formData.value.phone || "").replace(/\D/g, "");

    let rawPhoneDigits =
      ddiDigits === "55" ? dddDigits + phoneDigits : phoneDigits;

    if (ddiDigits === "55" && rawPhoneDigits.length > 0) {
      if (dddDigits.length !== 2) {
        showErrorToast(
          "Selecione Brasil (DDI +55) e preencha o DDD com 2 dígitos."
        );
        isLoading.value = false;
        return false;
      }
    }

    const phoneE164 =
      ddiDigits && rawPhoneDigits ? `+${ddiDigits}${rawPhoneDigits}` : null;

    const fd = new FormData();

    fd.append("userId", String(userId));
    fd.append("fullName", formData.value.fullName || "");
    fd.append("userName", formData.value.userName || "");
    fd.append("email", formData.value.email || "");
    fd.append("profession", formData.value.profession || "");
    fd.append("phoneIsWhatsapp", String(!!formData.value.phoneIsWhatsapp));
    fd.append("isWhatsappPublic", String(!!formData.value.isWhatsappPublic));
    fd.append("telegramUsername", formData.value.telegramUsername || "");
    fd.append("instagramUsername", formData.value.instagramUsername || "");
    fd.append("linkedInUsername", formData.value.linkedInUsername || "");
    fd.append("facebookUsername", formData.value.facebookUsername || "");

    fd.append("status", "Active");
    fd.append("country", formData.value.country || "");

    if (formData.value.birthDate) {
      fd.append("birthDate", formData.value.birthDate);
    }

    if (phoneE164) {
      fd.append("phoneE164", phoneE164);
    }

    if (additionalData && typeof additionalData.bio === "string") {
      fd.append("bio", additionalData.bio);
    }

    // ── PATCH: inclui marketingConsent no FormData enviado ao backend ────────
    if (additionalData && typeof additionalData.marketingConsent === "boolean") {
      fd.append("marketingConsent", String(additionalData.marketingConsent));
    }

    if (selectedFile.value) {
      fd.append("photoFile", selectedFile.value);
    }

    await userStore.updateUser(userId, fd);

    await showSuccessToast("Seu perfil foi atualizado com sucesso!");

    return true;
  } catch (error) {
    console.error("Erro ao atualizar perfil:", error);

    let userFriendlyMessage =
      "Erro de conexão ou servidor. Tente novamente em alguns segundos.";

    if (error.response?.status === 400 && error.response.data) {
      const data = error.response.data;

      if (data.errors && typeof data.errors === "object") {
        const errors = data.errors;
        let extractedError = null;

        if (errors.PhoneE164?.length > 0) {
          extractedError = errors.PhoneE164[0];
        } else if (errors.UserName?.length > 0) {
          extractedError = errors.UserName[0];
        } else if (errors.Email?.length > 0) {
          extractedError = errors.Email[0];
        } else {
          const firstErrorKey = Object.keys(errors).find(
            (key) => Array.isArray(errors[key]) && errors[key].length > 0
          );
          if (firstErrorKey) {
            extractedError = errors[firstErrorKey][0];
          }
        }

        if (extractedError) {
          userFriendlyMessage = extractedError;
        } else if (data.title) {
          userFriendlyMessage = data.title;
        } else {
          userFriendlyMessage =
            "Ocorreu um erro de validação nos dados. Verifique os campos.";
        }
      } else if (data.message) {
        userFriendlyMessage = data.message;
      } else if (data.title) {
        userFriendlyMessage = data.title;
      } else {
        userFriendlyMessage = "Verifique os campos e tente novamente.";
      }
    } else if (error.message) {
      userFriendlyMessage = error.message;
    }

    showErrorToast(userFriendlyMessage);

    return false;
  } finally {
    isLoading.value = false;
  }
}

function handleFileChange(event) {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    formData.value.photoUrl = URL.createObjectURL(file);
  }
}

function handleProfileFormSubmit(e) {
  const activeEl = document.activeElement;

  const isFocusInsideDdi =
    (ddiAutocompleteRef.value && ddiAutocompleteRef.value.contains(activeEl)) ||
    (ddiOptionsRef.value && ddiOptionsRef.value.contains(activeEl));

  if (isFocusInsideDdi) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  return handleUpdate();
}

defineExpose({ handleUpdate, canSubmit, closeForm, avatarSrc });
</script>

<template>
  <div class="form-container" v-show="props.isVisible">
    <template v-if="props.section === 'personal'">
      <h3 class="view-title">Dados Pessoais</h3>
      <p class="view-subtitle">Atualize sua foto, dados de contato e senha.</p>
    </template>

    <form @submit="handleProfileFormSubmit" id="profile-form">
      <!-- 🔹 SEÇÃO DADOS PESSOAIS -->
      <div class="form-grid" v-if="props.section === 'personal'">
        <div class="form-column">
          <div class="form-group items-center">
            <label>Foto de Perfil</label>
            <div class="photo-upload-container">
              <img
                :src="avatarSrc"
                alt="Avatar do usuário"
                class="avatar-preview"
              />
              <input
                id="photo-upload"
                type="file"
                @change="handleFileChange"
                accept="image/*"
                class="hidden"
              />
              <label for="photo-upload" class="btn btn-secondary btn-sm">
                Trocar foto
              </label>
            </div>
          </div>

          <div class="form-group form-group--disabled">
            <label for="fullName">Nome Completo</label>
            <input
              id="fullName"
              type="text"
              v-model="formData.fullName"
              class="input-base"
              disabled
            />
          </div>

          <div class="form-group mt-1">
            <label for="phone">Telefone</label>

            <div class="phone-row mb-2">
              <div class="ddi-autocomplete" ref="ddiAutocompleteRef">
                <input
                  type="text"
                  class="input-base ddi-input"
                  v-model="ddiSearchTerm"
                  @focus="handleDdiFocus"
                  @input="handleDdiInput"
                  @keydown="handleDdiKeydown"
                  @blur="handleDdiBlur"
                  placeholder="+55"
                />

                <ul
                  v-if="isAutocompleteVisible"
                  class="ddi-options"
                  ref="ddiOptionsRef"
                >
                  <li
                    v-for="(country, index) in filteredCountries"
                    :key="country.code"
                    @mousedown.prevent="selectCountry(country)"
                    @mouseenter="activeIndex = index"
                    :class="{ 'is-active': index === activeIndex }"
                  >
                    <span>{{ country.name }} (+{{ country.code }})</span>
                  </li>
                  <li v-if="!filteredCountries.length" class="ddi-no-results">
                    Nenhum país encontrado.
                  </li>
                </ul>
              </div>

              <input
                v-if="formData.ddi === '55'"
                id="ddd"
                type="tel"
                v-model="formData.ddd"
                class="input-base ddd-input"
                placeholder="DDD"
                maxlength="2"
              />

              <input
                id="phone"
                type="tel"
                v-model="formData.phone"
                class="input-base phone-number-input"
                placeholder="99999-9999"
              />
            </div>
          </div>

          <div class="form-group-row">
            <div class="form-group flex-1">
              <label for="country">País (opcional)</label>
              <input
                id="country"
                type="text"
                v-model="formData.country"
                class="input-base"
                placeholder="Ex: Brasil"
              />
            </div>

            <div class="form-group flex-1">
              <label for="profession">Profissão (opcional)</label>
              <input
                id="profession"
                type="text"
                v-model="formData.profession"
                class="input-base"
              />
            </div>
          </div>
        </div>

        <div class="form-column">
          <div class="form-group form-group--disabled">
            <label for="userName">Nome de Usuário</label>
            <input
              id="userName"
              type="text"
              v-model="formData.userName"
              class="input-base"
              required
              disabled
            />
          </div>

          <div class="form-group form-group--disabled">
            <label for="email">E-mail</label>
            <input
              id="email"
              type="email"
              v-model="formData.email"
              class="input-base"
              required
              disabled
            />
          </div>

          <div class="form-group-row mt-3">
            <div class="form-group flex-1">
              <label for="birthDate">Data de Nascimento</label>
              <input
                id="birthDate"
                type="date"
                v-model="formData.birthDate"
                class="input-base"
              />
            </div>
          </div>

          <div class="form-group-row mt-4"></div>
        </div>
      </div>

      <!-- 🔹 SEÇÃO REDES SOCIAIS -->
      <div v-else-if="props.section === 'social'">
        <SocialLinksForm :formData="formData" />
      </div>

      <!-- ── MUDANÇA: bloco de grupos removido daqui.
           Grupos agora são exibidos em AdditionalProfileDetails.vue,
           visíveis apenas ao clicar na aba "Demais dados". ────────────────── -->
    </form>
  </div>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.form-container {
  padding: 0.3rem;
  height: 100%;
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
}

.view-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}
.view-subtitle {
  font-size: 0.9rem;
  color: var(--text-light);
  margin-top: 0.2rem;
  margin-bottom: 1.2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.9rem;
}
@media (min-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.form-group.items-center {
  align-items: center;
}

.form-group label {
  margin-bottom: 0.35rem;
}

.form-group-row {
  display: flex;
  gap: 0.9rem;
  width: 100%;
}
.flex-1 {
  flex: 1;
}

/* Grupo desabilitado: rótulo levemente esmaecido junto com o campo */
.form-group--disabled label {
  color: var(--text-muted);
}

.photo-upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
}
.avatar-preview {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color);
}

.btn-sm {
  padding: 0.4rem 0.75rem;
  font-size: 0.86rem;
}

.phone-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}
.ddi-autocomplete {
  position: relative;
  flex: 0 0 120px;
}
.ddi-input {
  width: 100%;
}
.ddd-input {
  flex: 0 0 46px;
  max-width: 46px;
  text-align: center;
}
.phone-number-input {
  flex: 1;
}

.ddi-options {
  position: absolute;
  top: 100%;
  left: 0;
  width: 240px;
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  padding: 0.4rem;
  max-height: 190px;
  overflow-y: auto;
  list-style: none;
}
.ddi-options li {
  padding: 0.45rem;
  cursor: pointer;
  border-radius: 6px;
  font-size: 0.88rem;
}
.ddi-options li:hover {
  background-color: var(--neutral-100);
}
.ddi-options li.is-active {
  background-color: var(--neutral-100);
}
.ddi-no-results {
  padding: 0.45rem;
  font-size: 0.85rem;
  text-align: center;
}

.terms-checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: pointer;
}

.terms-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-dark);
}

/* ── MUDANÇA: .group-tags-container e .group-tag removidos daqui.
   Os estilos de grupos vivem apenas em AdditionalProfileDetails.vue. ──────── */

.password-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.password-toggle-btn {
  position: absolute;
  right: 0.6rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-light);
}
.input-error {
  border-color: var(--danger) !important;
}

.label-row {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.password-hints {
  margin-top: 0.35rem;
  font-size: 0.82rem;
  color: var(--text-light);
  line-height: 1.25;
}
.password-hints .met {
  color: var(--success);
  text-decoration: line-through;
}

.hidden {
  display: none;
}
</style>